import React, { useState, useEffect } from 'react';
import { Zap, Play, Settings, CheckCircle, AlertCircle, Clock, Database, FileText } from 'lucide-react';
import { Card } from '../../../common/Card';
import { Button } from '../../../common/Button';
import { useAuth } from '../../../../contexts/AuthContext';
import ruleGroupEngineService, { 
  SystemRuleTypeInfo, 
  RuleGroupParameters, 
  SystemRuleGroup, 
  RuleGroupResult,
  SystemRuleTestResult,
  SystemRuleTestRequest,
  RuleGroupValidationResult
} from '../../../../services/ruleGroupEngineService';
import { logger } from '../../../../services/loggingService';

type TestTab = 'rulegroups' | 'systemrules' | 'validation';

interface TestState {
  loading: boolean;
  executing: boolean;
  error?: string;
  ruleGroups: SystemRuleGroup[];
  systemRuleTypes: SystemRuleTypeInfo[];
  results: RuleGroupResult[];
  systemRuleResults: SystemRuleTestResult[];
  validationResults: RuleGroupValidationResult[];
}

export const RuleGroupEngineTest: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TestTab>('rulegroups');
  const [testState, setTestState] = useState<TestState>({
    loading: false,
    executing: false,
    ruleGroups: [],
    systemRuleTypes: [],
    results: [],
    systemRuleResults: [],
    validationResults: []
  });

  // Form state
  const [selectedRuleGroupId, setSelectedRuleGroupId] = useState<number>(0);
  const [selectedSystemRule, setSelectedSystemRule] = useState<number>(1);
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [debugMode, setDebugMode] = useState<boolean>(true);
  const [maxRecords, setMaxRecords] = useState<number>(100);
  const [sqlFilter, setSqlFilter] = useState<string>('');
  const [sqlSorting, setSqlSorting] = useState<string>('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    if (!user?.companyNo) return;

    setTestState(prev => ({ ...prev, loading: true, error: undefined }));
    
    try {
      // Load rule groups and system rule types in parallel
      const [ruleGroups, systemRuleTypes] = await Promise.all([
        ruleGroupEngineService.getRuleGroups(user.companyNo).catch(() => []),
        ruleGroupEngineService.getSystemRuleTypes(user.companyNo).catch(() => [])
      ]);

      setTestState(prev => ({
        ...prev,
        loading: false,
        ruleGroups,
        systemRuleTypes
      }));

      //await logger.logUserAction('RuleGroupEngineTest', 'LoadInitialData', {
      //  ruleGroupCount: ruleGroups.length,
      //  systemRuleTypeCount: systemRuleTypes.length
      //});

    } catch (error: any) {
      const errorMsg = error.message || 'Failed to load initial data';
      setTestState(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMsg 
      }));
      
      await logger.logException('RuleGroupEngineTest', error);
    }
  };

  const executeRuleGroup = async () => {
    if (!user?.companyNo || selectedRuleGroupId <= 0) {
      alert('Velg en regelgruppe å teste');
      return;
    }

    setTestState(prev => ({ ...prev, executing: true, error: undefined }));

    try {
      const parameters: RuleGroupParameters = ruleGroupEngineService.createSampleParameters(
        user.companyNo,
        selectedRuleGroupId,
        warehouseId,
        sqlFilter || undefined
      );

      parameters.debugMode = debugMode;

      //await logger.logUserAction('RuleGroupEngineTest', 'ExecuteRuleGroup', {
      //  ruleGroupID: selectedRuleGroupId,
      //  warehouseID: warehouseId,
      //  debugMode: debugMode
      //});

      const result = await ruleGroupEngineService.executeRuleGroup(user.companyNo, parameters);

      setTestState(prev => ({
        ...prev,
        executing: false,
        results: [result, ...prev.results.slice(0, 9)] // Keep last 10 results
      }));

      //await logger.logUserAction('RuleGroupEngineTest', 'ExecuteRuleGroupComplete', {
      //  ruleGroupID: selectedRuleGroupId,
      //  success: result.success,
      //  totalRulesExecuted: result.totalRulesExecuted,
      //  totalRecordsAffected: result.totalRecordsAffected
      //});

    } catch (error: any) {
      const errorMsg = error.message || 'Failed to execute rule group';
      setTestState(prev => ({ 
        ...prev, 
        executing: false, 
        error: errorMsg 
      }));
      
      await logger.logException('RuleGroupEngineTest', error);
    }
  };

  const testSystemRule = async () => {
    if (!user?.companyNo) return;

    setTestState(prev => ({ ...prev, executing: true, error: undefined }));

    try {
      const testRequest: SystemRuleTestRequest = {
        ruleNo: selectedSystemRule,
        warehouseID: warehouseId,
        sqlExtraFilter: sqlFilter || undefined,
        sqlSorting: sqlSorting || undefined,
        maxRecords: maxRecords,
        parameters: {
          debugMode: debugMode,
          testMode: true
        }
      };

      //await logger.logUserAction('RuleGroupEngineTest', 'TestSystemRule', {
      //  ruleNo: selectedSystemRule,
      //  warehouseID: warehouseId
      //});

      const result = await ruleGroupEngineService.testSystemRule(user.companyNo, testRequest);

      setTestState(prev => ({
        ...prev,
        executing: false,
        systemRuleResults: [result, ...prev.systemRuleResults.slice(0, 9)]
      }));

    } catch (error: any) {
      const errorMsg = error.message || 'Failed to test system rule';
      setTestState(prev => ({ 
        ...prev, 
        executing: false, 
        error: errorMsg 
      }));
      
      await logger.logException('RuleGroupEngineTest', error);
    }
  };

  const validateRuleGroup = async () => {
    if (!user?.companyNo || selectedRuleGroupId <= 0) {
      alert('Velg en regelgruppe å validere');
      return;
    }

    setTestState(prev => ({ ...prev, executing: true, error: undefined }));

    try {
      const parameters: RuleGroupParameters = ruleGroupEngineService.createSampleParameters(
        user.companyNo,
        selectedRuleGroupId,
        warehouseId
      );

      const result = await ruleGroupEngineService.validateRuleGroup(user.companyNo, parameters);

      setTestState(prev => ({
        ...prev,
        executing: false,
        validationResults: [result, ...prev.validationResults.slice(0, 9)]
      }));

    } catch (error: any) {
      const errorMsg = error.message || 'Failed to validate rule group';
      setTestState(prev => ({ 
        ...prev, 
        executing: false, 
        error: errorMsg 
      }));
    }
  };

  const formatExecutionTime = (timeSpan: string): string => {
    return ruleGroupEngineService.formatExecutionTime(timeSpan);
  };

  const renderRuleGroupsTab = () => (
    <div className="space-y-6">
      {/* Configuration */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings size={20} />
          Test Konfigurasjon - Regelgrupper
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Regelgruppe
            </label>
            <select
              value={selectedRuleGroupId}
              onChange={(e) => setSelectedRuleGroupId(parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            >
              <option value={0}>Velg regelgruppe...</option>
              {testState.ruleGroups.map((rg) => (
                <option key={rg.ruleGroupID} value={rg.ruleGroupID}>
                  {rg.ruleGroupID} - {rg.ruleGroupName} ({rg.activeYesNo ? 'Aktiv' : 'Inaktiv'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lager ID
            </label>
            <input
              type="text"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              placeholder="La stå tom for standard"
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SQL Filter (valgfritt)
            </label>
            <input
              type="text"
              value={sqlFilter}
              onChange={(e) => setSqlFilter(e.target.value)}
              placeholder="WHERE condition..."
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SQL Sortering (valgfritt)
            </label>
            <input
              type="text"
              value={sqlSorting}
              onChange={(e) => setSqlSorting(e.target.value)}
              placeholder="ORDER BY clause..."
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maks antall poster
            </label>
            <input
              type="number"
              value={maxRecords}
              onChange={(e) => setMaxRecords(parseInt(e.target.value) || 100)}
              min={1}
              max={10000}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            />
          </div>

          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={debugMode}
                onChange={(e) => setDebugMode(e.target.checked)}
                disabled={testState.executing}
                className="mr-2"
              />
              Debug modus
            </label>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <Button
            onClick={executeRuleGroup}
            disabled={testState.executing || selectedRuleGroupId <= 0}
            className="flex items-center gap-2"
          >
            <Play size={16} />
            {testState.executing ? 'Kjører...' : 'Kjør Regelgruppe'}
          </Button>

          <Button
            onClick={validateRuleGroup}
            disabled={testState.executing || selectedRuleGroupId <= 0}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <CheckCircle size={16} />
            Valider Regelgruppe
          </Button>
        </div>
      </Card>

      {/* Results */}
      {testState.results.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Resultater - Regelgrupper</h3>
          <div className="space-y-4">
            {testState.results.map((result, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <CheckCircle size={20} className="text-green-600" />
                    ) : (
                      <AlertCircle size={20} className="text-red-600" />
                    )}
                    <span className={`font-medium ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                      {result.success ? 'Suksess' : 'Feil'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <Clock size={16} />
                    {formatExecutionTime(result.totalExecutionTime)}
                  </div>
                </div>

                <div className="text-sm text-gray-700 mb-3">
                  {result.message}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Regler utført:</span>
                    <div className="font-medium">{result.totalRulesExecuted}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Suksess:</span>
                    <div className="font-medium text-green-600">{result.totalRulesSucceeded}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Feil:</span>
                    <div className="font-medium text-red-600">{result.totalRulesFailed}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Poster påvirket:</span>
                    <div className="font-medium">{result.totalRecordsAffected}</div>
                  </div>
                </div>

                {result.errors.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-red-600">Feil:</span>
                    <div className="text-sm text-red-600 mt-1">
                      {result.errors.map((error, idx) => (
                        <div key={idx}>• {error}</div>
                      ))}
                    </div>
                  </div>
                )}

                {result.warnings.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-yellow-600">Advarsler:</span>
                    <div className="text-sm text-yellow-600 mt-1">
                      {result.warnings.map((warning, idx) => (
                        <div key={idx}>• {warning}</div>
                      ))}
                    </div>
                  </div>
                )}

                {result.ruleResults.length > 0 && (
                  <details className="mt-3">
                    <summary className="text-sm font-medium text-gray-600 cursor-pointer">
                      Detaljerte regelresultater ({result.ruleResults.length})
                    </summary>
                    <div className="mt-2 space-y-2">
                      {result.ruleResults.map((ruleResult, idx) => (
                        <div key={idx} className="text-xs border-l-2 border-gray-200 pl-3">
                          <div className="flex items-center gap-2">
                            {ruleResult.success ? '✅' : '❌'}
                            <span className="font-medium">Regel {ruleResult.ruleID}: {ruleResult.ruleName}</span>
                            <span className="text-gray-500">({formatExecutionTime(ruleResult.executionTime)})</span>
                          </div>
                          <div className="text-gray-600">{ruleResult.message}</div>
                          {ruleResult.recordsAffected > 0 && (
                            <div className="text-gray-500">Poster påvirket: {ruleResult.recordsAffected}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  const renderSystemRulesTab = () => (
    <div className="space-y-6">
      {/* Configuration */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Zap size={20} />
          Test Konfigurasjon - Systemregler
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Systemregel
            </label>
            <select
              value={selectedSystemRule}
              onChange={(e) => setSelectedSystemRule(parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            >
              {testState.systemRuleTypes.map((rule) => (
                <option key={rule.ruleNo} value={rule.ruleNo}>
                  {rule.ruleNo} - {rule.name}
                </option>
              ))}
            </select>
            <div className="mt-1 text-sm text-gray-500">
              {testState.systemRuleTypes.find(r => r.ruleNo === selectedSystemRule)?.description}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lager ID
            </label>
            <input
              type="text"
              value={warehouseId}
              onChange={(e) => setWarehouseId(e.target.value)}
              placeholder="La stå tom for standard"
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SQL Filter (valgfritt)
            </label>
            <input
              type="text"
              value={sqlFilter}
              onChange={(e) => setSqlFilter(e.target.value)}
              placeholder="WHERE condition..."
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maks antall poster
            </label>
            <input
              type="number"
              value={maxRecords}
              onChange={(e) => setMaxRecords(parseInt(e.target.value) || 100)}
              min={1}
              max={1000}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            />
          </div>
        </div>

        <div className="mt-6">
          <Button
            onClick={testSystemRule}
            disabled={testState.executing}
            className="flex items-center gap-2"
          >
            <Play size={16} />
            {testState.executing ? 'Tester...' : 'Test Systemregel'}
          </Button>
        </div>
      </Card>

      {/* System Rule Info */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Tilgjengelige Systemregler</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testState.systemRuleTypes.map((rule) => (
            <div
              key={rule.ruleNo}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                selectedSystemRule === rule.ruleNo
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedSystemRule(rule.ruleNo)}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                  {rule.ruleNo}
                </div>
                <div className="font-medium text-sm">{rule.name}</div>
              </div>
              <div className="text-xs text-gray-600">{rule.description}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* System Rule Results */}
      {testState.systemRuleResults.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Resultater - Systemregler</h3>
          <div className="space-y-4">
            {testState.systemRuleResults.map((result, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <CheckCircle size={20} className="text-green-600" />
                    ) : (
                      <AlertCircle size={20} className="text-red-600" />
                    )}
                    <div>
                      <span className="font-medium">Regel {result.ruleNo}</span>
                      <div className="text-sm text-gray-600">{result.ruleName}</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <Clock size={16} />
                    {formatExecutionTime(result.executionTime)}
                  </div>
                </div>

                <div className="text-sm text-gray-700 mb-3">
                  {result.message}
                </div>

                <div className="text-sm">
                  <span className="text-gray-500">Poster påvirket:</span>
                  <span className="font-medium ml-1">{result.recordsAffected}</span>
                </div>

                {result.errors.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-red-600">Feil:</span>
                    <div className="text-sm text-red-600 mt-1">
                      {result.errors.map((error, idx) => (
                        <div key={idx}>• {error}</div>
                      ))}
                    </div>
                  </div>
                )}

                {result.warnings.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-yellow-600">Advarsler:</span>
                    <div className="text-sm text-yellow-600 mt-1">
                      {result.warnings.map((warning, idx) => (
                        <div key={idx}>• {warning}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  const renderValidationTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <CheckCircle size={20} />
          Validering av Regelgrupper
        </h3>
        <p className="text-gray-600 mb-4">
          Valider regelgrupper uten å kjøre dem. Dette sjekker at alle regler eksisterer, 
          er konfigurert riktig og kan kjøres uten feil.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Regelgruppe å validere
            </label>
            <select
              value={selectedRuleGroupId}
              onChange={(e) => setSelectedRuleGroupId(parseInt(e.target.value))}
              className="w-full max-w-md p-2 border border-gray-300 rounded-md"
              disabled={testState.executing}
            >
              <option value={0}>Velg regelgruppe...</option>
              {testState.ruleGroups.map((rg) => (
                <option key={rg.ruleGroupID} value={rg.ruleGroupID}>
                  {rg.ruleGroupID} - {rg.ruleGroupName}
                </option>
              ))}
            </select>
          </div>

          <Button
            onClick={validateRuleGroup}
            disabled={testState.executing || selectedRuleGroupId <= 0}
            className="flex items-center gap-2"
          >
            <CheckCircle size={16} />
            {testState.executing ? 'Validerer...' : 'Valider Regelgruppe'}
          </Button>
        </div>
      </Card>

      {testState.validationResults.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Valideringsresultater</h3>
          <div className="space-y-4">
            {testState.validationResults.map((result, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  {result.isValid ? (
                    <CheckCircle size={20} className="text-green-600" />
                  ) : (
                    <AlertCircle size={20} className="text-red-600" />
                  )}
                  <div>
                    <span className={`font-medium ${result.isValid ? 'text-green-800' : 'text-red-800'}`}>
                      {result.isValid ? 'Gyldig' : 'Ugyldig'}
                    </span>
                    <div className="text-sm text-gray-600">
                      {result.ruleGroupName} ({result.ruleCount} regler)
                    </div>
                  </div>
                </div>

                {result.errors.length > 0 && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-red-600">Feil:</span>
                    <div className="text-sm text-red-600 mt-1">
                      {result.errors.map((error, idx) => (
                        <div key={idx}>• {error}</div>
                      ))}
                    </div>
                  </div>
                )}

                {result.warnings.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-yellow-600">Advarsler:</span>
                    <div className="text-sm text-yellow-600 mt-1">
                      {result.warnings.map((warning, idx) => (
                        <div key={idx}>• {warning}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="flex items-center gap-3">
          <Zap size={32} />
          <div>
            <h2 className="text-xl font-bold">RuleGroupEngine Test</h2>
            <p className="text-purple-100">Test systemregler og regelgrupper</p>
          </div>
        </div>
      </Card>

      {testState.error && (
        <Card className="p-4 bg-red-50 border-red-200">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle size={20} />
            <span className="font-medium">Feil:</span>
            <span>{testState.error}</span>
          </div>
        </Card>
      )}

      {/* Loading */}
      {testState.loading && (
        <Card className="p-6 text-center">
          <div className="flex items-center justify-center gap-2">
            <Database className="animate-spin" size={20} />
            <span>Laster data...</span>
          </div>
        </Card>
      )}

      {/* Tab Navigation */}
      {!testState.loading && (
        <Card className="p-1">
          <div className="flex space-x-1">
            {[
              { id: 'rulegroups' as TestTab, name: 'Regelgrupper', icon: Database },
              { id: 'systemrules' as TestTab, name: 'Systemregler', icon: Zap },
              { id: 'validation' as TestTab, name: 'Validering', icon: CheckCircle }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                }`}
              >
                <tab.icon size={16} />
                {tab.name}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Tab Content */}
      {!testState.loading && (
        <>
          {activeTab === 'rulegroups' && renderRuleGroupsTab()}
          {activeTab === 'systemrules' && renderSystemRulesTab()}
          {activeTab === 'validation' && renderValidationTab()}
        </>
      )}

      {/* Info Panel */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <FileText className="text-blue-600 mt-1 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">RuleGroupEngine Testing</p>
            <div className="space-y-1 text-xs">
              <div>• <strong>Regelgrupper:</strong> Kjør komplette regelgrupper som du har konfigurert i systemet</div>
              <div>• <strong>Systemregler:</strong> Test individuelle systemregler (1=Ny plukking, 2=Tildel operatør, 4=Batch plukking, 6=Utlegg, 7=Påfylling)</div>
              <div>• <strong>Validering:</strong> Kontroller at regelgrupper er konfigurert riktig uten å kjøre dem</div>
              <div>• Alle tester kjøres i debug-modus og logger aktivitet</div>
              <div>• Resultater vises i kronologisk rekkefølge (nyest først)</div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
